import { useId, useState } from 'react'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import { isLiabilityType, type AccountType } from '@/api/accounts'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Select } from '@/components/ui/select'
import {
  NO_ACCOUNT_FILTERS,
  hasActiveAccountFilters,
  type AccountFilters,
  type AccountSide,
} from '@/lib/accountFilters'
import { cn } from '@/lib/utils'

const SIDES: [string, AccountSide][] = [
  ['All', 'ALL'],
  ['What you have', 'ASSETS'],
  ['What you owe', 'LIABILITIES'],
]

const ACCOUNT_TYPE_FILTER_LABELS: Record<AccountType, string> = {
  BANK: 'Bank & savings',
  CASH: 'Cash',
  DIGITAL_WALLET: 'Digital wallets',
  INVESTMENT: 'Investments',
  RECEIVABLE: 'Receivables',
  PROPERTY: 'Property',
  LOAN: 'Loans',
  CREDIT_CARD: 'Credit cards',
  PAYABLE: 'Payables',
}

// A type from the other side would match nothing, so switching side drops it.
function typeOnSide(type: AccountType | '', side: AccountSide): AccountType | '' {
  if (!type || side === 'ALL') return type
  return isLiabilityType(type) === (side === 'LIABILITIES') ? type : ''
}

function MoreFilters({
  value,
  set,
  types,
  stacked,
}: {
  value: AccountFilters
  set: (patch: Partial<AccountFilters>) => void
  types: AccountType[]
  stacked?: boolean
}) {
  return (
    <div className={cn('flex gap-3', stacked ? 'flex-col' : 'flex-wrap items-center')}>
      <Select
        aria-label="Account type"
        value={value.type}
        onChange={(v) => set({ type: v as AccountType | '' })}
        className={stacked ? 'w-full' : 'w-44'}
      >
        <option value="">All types</option>
        {types.map((t) => (
          <option key={t} value={t}>
            {ACCOUNT_TYPE_FILTER_LABELS[t]}
          </option>
        ))}
      </Select>
      <label className="flex items-center gap-2 text-sm text-[var(--fl-text-1)]">
        <Checkbox checked={value.hideZero} onChange={(e) => set({ hideZero: e.target.checked })} />
        Hide zero balances
      </label>
      <label className="flex items-center gap-2 text-sm text-[var(--fl-text-1)]">
        <Checkbox
          checked={value.showArchived}
          onChange={(e) => set({ showArchived: e.target.checked })}
        />
        Show archived
      </label>
    </div>
  )
}

function MoreFiltersSheet({
  onClose,
  children,
}: {
  onClose: () => void
  children: React.ReactNode
}) {
  const titleId = useId()
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 md:hidden"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        className="flex w-full flex-col rounded-t-[20px] bg-[var(--fl-surface)] shadow-[rgba(0,0,0,0.15)_0px_8px_40px] motion-safe:animate-fade-slide-up"
      >
        <div
          aria-hidden="true"
          className="mx-auto mt-2 h-1 w-10 rounded-full bg-[var(--fl-border)]"
        />
        <div className="flex items-center justify-between px-4 pt-2">
          <h2 id={titleId} className="font-serif text-xl font-semibold text-[var(--fl-heading)]">
            Filters
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-lg text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-4 py-3">{children}</div>
        <div className="border-t border-[var(--fl-divider)] px-4 pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
          <Button className="h-11 w-full" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Search and the side pills always show; type, ₹0 and archived sit inline on desktop and in a sheet on a phone. */
export function AccountFiltersBar({
  value,
  onChange,
  types,
}: {
  value: AccountFilters
  onChange: (next: AccountFilters) => void
  /** The account types to offer — only those the household has on the chosen side. */
  types: AccountType[]
}) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const set = (patch: Partial<AccountFilters>) => onChange({ ...value, ...patch })
  const hiddenActive = value.type !== '' || value.hideZero || value.showArchived

  return (
    <div className="mb-3 flex flex-col gap-2.5 md:mb-6 md:flex-row md:flex-wrap md:items-center md:gap-3">
      <div className="flex items-center gap-2">
        <label className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-xl border border-[var(--fl-border)] bg-[var(--fl-surface)] px-3 text-[var(--fl-text-3)] focus-within:border-[var(--fl-primary)] md:w-72 md:flex-none">
          <Search size={16} aria-hidden="true" className="shrink-0" />
          <input
            type="search"
            value={value.q}
            maxLength={50}
            onChange={(e) => set({ q: e.target.value })}
            placeholder="Search accounts"
            aria-label="Search accounts"
            className="min-w-0 flex-1 bg-transparent text-sm text-[var(--fl-text-1)] outline-none placeholder:text-[var(--fl-text-3)]"
          />
        </label>
        <button
          type="button"
          aria-label="More filters"
          onClick={() => setSheetOpen(true)}
          className="relative grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-[var(--fl-border)] bg-[var(--fl-surface)] text-[var(--fl-text-2)] md:hidden"
        >
          <SlidersHorizontal size={17} />
          {hiddenActive && (
            <span
              aria-hidden="true"
              className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[var(--fl-primary)]"
            />
          )}
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {SIDES.map(([label, side]) => {
          const active = value.side === side
          return (
            <button
              key={side}
              type="button"
              aria-pressed={active}
              onClick={() => set({ side, type: typeOnSide(value.type, side) })}
              className={cn(
                'h-8 rounded-full border px-3 text-[13px] font-medium transition-colors',
                active
                  ? 'border-[var(--fl-text-1)] bg-[var(--fl-text-1)] text-[var(--fl-surface)]'
                  : 'border-[var(--fl-border)] bg-[var(--fl-surface)] text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]'
              )}
            >
              {label}
            </button>
          )
        })}
      </div>

      <div className="hidden md:ml-auto md:block">
        <MoreFilters value={value} set={set} types={types} />
      </div>

      {hasActiveAccountFilters(value) && (
        <button
          type="button"
          onClick={() => onChange(NO_ACCOUNT_FILTERS)}
          className="h-8 self-start px-1.5 text-[12.5px] font-medium text-[var(--fl-text-2)] underline underline-offset-2 hover:text-[var(--fl-text-1)] md:self-auto"
        >
          Clear all
        </button>
      )}

      {sheetOpen && (
        <MoreFiltersSheet onClose={() => setSheetOpen(false)}>
          <MoreFilters value={value} set={set} types={types} stacked />
        </MoreFiltersSheet>
      )}
    </div>
  )
}
